import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Booking, BookingCancellationEntity } from "../types/booking.entity";
import {
  FaUser,
  FaCalendarAlt,
  FaIdBadge,
  FaRegStickyNote,
  FaInfoCircle,
  FaHashtag,
  FaArrowLeft,
} from "react-icons/fa";
import { useGetUsersTransactionsQuery } from "src/features/users-transactions/user-transactions.service";
import { Amount } from "src/components/Amount";
import { formatScreamingSnakeCase } from "src/utils/util";
import {
  transactionStatusStyles,
  transactionTypeStyles,
} from "src/features/users-transactions/utils/transaction-style";
import { TransactionLabelPill } from "src/features/users-transactions/components/transactions-table/label";
import { Badge } from "src/features/users-transactions/components/transactions-table/badge";

type Props = {
  booking: Booking & { id: string };
  cancellation: (BookingCancellationEntity & { id: string }) | null;
  onBack?: () => void;
};

const badge = "inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium";
const coachTypeClass = (t: Booking["coachType"]) =>
  t === "PRO" ? "bg-indigo-100 text-indigo-700 border-indigo-200" : "bg-orange-100 text-orange-700 border-orange-200";
const statusClass = (s: Booking["bookingStatus"]) =>
  ((
    {
      INIT: "bg-amber-100 text-amber-700 border-amber-200",
      CANCELLED: "bg-red-100 text-red-700 border-red-200",
      ACCEPTED: "bg-emerald-100 text-emerald-700 border-emerald-200",
      REJECTED: "bg-rose-100 text-rose-700 border-rose-200",
      COMPLETED: "bg-blue-100 text-blue-700 border-blue-200",
      EXPIRED: "bg-gray-100 text-gray-700 border-gray-200",
    } as const
  )[s]);

const formatDate = (ts: any) => new Date(ts?.toDate?.() || ts).toLocaleDateString();
const formatTime = (ts: any) =>
  new Date(ts?.toDate?.() || ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const BookingDetails: React.FC<Props> = ({ booking, cancellation, onBack }) => {
  const { data: relatedTransactions } = useGetUsersTransactionsQuery({ where: { bookingId: booking.id }, offset: "" });
  const sortedTransactions = useMemo((): typeof relatedTransactions => {
    if (!relatedTransactions) return [];
    const getDate = (tx: any) => new Date(tx?.createdAt?.toDate?.() || tx?.createdAt) as Date;
    return [...relatedTransactions].sort((a: any, b: any) => getDate(a).getTime() - getDate(b).getTime());
  }, [relatedTransactions]);

  const learnerTransactions = useMemo(() => {
    return (sortedTransactions ?? []).filter((tx: any) => tx.userId === booking.learnerId);
  }, [sortedTransactions, booking.learnerId]);

  const coachTransactions = useMemo(() => {
    return (sortedTransactions ?? []).filter((tx: any) => tx.userId === booking.coachId);
  }, [sortedTransactions, booking.coachId]);
  const [isVisible, setIsVisible] = useState(false);
  const [showCancelCard, setShowCancelCard] = useState(false);
  const [showLearnerBox, setShowLearnerBox] = useState(false);
  const [showCoachBox, setShowCoachBox] = useState(false);
  const learnerBoxRef = useRef<HTMLDivElement>(null);
  const coachBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (cancellation) {
      const t = setTimeout(() => setShowCancelCard(true), 60);
      return () => clearTimeout(t);
    }
  }, [cancellation]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === learnerBoxRef.current) setShowLearnerBox(true);
            if (entry.target === coachBoxRef.current) setShowCoachBox(true);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (learnerBoxRef.current) observer.observe(learnerBoxRef.current);
    if (coachBoxRef.current) observer.observe(coachBoxRef.current);

    return () => observer.disconnect();
  }, []);

  const baseItemClasses = "transform transition-all duration-300 ease-out";
  const hiddenItemClasses = "opacity-0 translate-y-1";
  const visibleItemClasses = "opacity-100 translate-y-0";
  const cx = (extra: string = "") =>
    `${baseItemClasses} ${isVisible ? visibleItemClasses : hiddenItemClasses} ${extra}`.trim();
  const delayStyle = (ms: number) => ({ transitionDelay: `${ms}ms` } as React.CSSProperties);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        {/* Animation styles provided by animate.css (animate__zoomIn) */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Booking Details</h1>
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1"
            >
              <FaArrowLeft />
              Back
            </button>
          )}
        </div>

        <div
          className={
            "bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transform transition-all duration-300 ease-out " +
            (isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95")
          }
        >
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <span className={cx(`${badge} ${statusClass(booking.bookingStatus)}`)} style={delayStyle(60)}>
              {booking.bookingStatus}
            </span>
            <span className={cx(`${badge} ${coachTypeClass(booking.coachType)}`)} style={delayStyle(120)}>
              {booking.coachType}
            </span>
            <span className={cx(`${badge} bg-cyan-100 text-cyan-700 border-cyan-200`)} style={delayStyle(180)}>
              Lang: {booking.langCode}
            </span>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={cx("flex items-start gap-3")} style={delayStyle(80)}>
              <FaHashtag className="mt-1 text-gray-500" />
              <div>
                <div className="text-sm text-gray-500">Booking ID</div>
                <div className="font-mono text-sm">{booking.id}</div>
              </div>
            </div>
            <div className={cx("flex items-center gap-3")} style={delayStyle(120)}>
              <FaUser className="text-blue-500" />
              <span className="font-semibold">Coach:</span>
              <Link
                to={`/users/${booking.coachId}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
              >
                {booking.coachId}
              </Link>
            </div>
            <div className={cx("flex items-center gap-3")} style={delayStyle(160)}>
              <FaIdBadge className="text-emerald-500" />
              <span className="font-semibold">Slot:</span>
              <Link
                to={`/slots/${booking.slotId}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
              >
                {booking.slotId}
              </Link>
            </div>

            <div className={cx("flex items-center gap-3")} style={delayStyle(200)}>
              <FaUser className="text-purple-500" />
              <span className="font-semibold">Learner:</span>
              <Link
                to={`/users/${booking.learnerId}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
              >
                {booking.learnerId}
              </Link>
            </div>

            <div className={cx("flex items-center gap-3")} style={delayStyle(240)}>
              <FaCalendarAlt className="text-emerald-600" />
              <span className="font-semibold">Created:</span>
              <span className="leading-tight">
                <div>{formatDate(booking.createdAt)}</div>
                <div>{formatTime(booking.createdAt)}</div>
              </span>
            </div>

            <div className={cx("flex items-center gap-3")} style={delayStyle(280)}>
              <FaCalendarAlt className="text-teal-600" />
              <span className="font-semibold">When:</span>
              <span className="leading-tight">
                <div>{formatDate(booking.date)}</div>
                <div>{formatTime(booking.date)}</div>
              </span>
            </div>

            {booking.note && (
              <div className={cx("flex items-start gap-3 md:col-span-2")} style={delayStyle(320)}>
                <FaRegStickyNote className="text-amber-600 mt-1" />
                <div>
                  <div className="font-semibold">Note</div>
                  <div className="text-slate-700">{booking.note}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {cancellation && (
          <div className={`mt-6 ${showCancelCard ? "animate__animated animate__zoomIn" : "opacity-0"}`}>
            <div className="bg-gradient-to-r from-rose-50 to-red-50 border border-red-200 rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b border-red-200 flex items-center gap-3">
                <FaInfoCircle className="text-red-600" />
                <h2 className="text-lg font-semibold text-red-700 m-0">Booking Cancellation</h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm text-slate-500">Cancelled By</div>
                  <div className="font-semibold">{cancellation.cancelledBy}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Status</div>
                  <div className="font-semibold">{cancellation.status}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Type</div>
                  <div className="font-semibold">{cancellation.cancellationType.replaceAll("_", " ")}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Refund %</div>
                  <div className="font-semibold">{cancellation.learnerRefundPercentage ?? 0}%</div>
                </div>
                {cancellation.karmaGift != null && (
                  <div>
                    <div className="text-sm text-slate-500">Gift</div>
                    <div className="font-semibold">{cancellation.karmaGift}</div>
                  </div>
                )}
                {cancellation.reason && (
                  <div className={`${cancellation.karmaGift != null ? "" : "md:col-span-2"}`}>
                    <div className="text-sm text-slate-500">Reason</div>
                    <div className="font-semibold">{cancellation.reason}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-slate-500">Created</div>
                  <div className="font-semibold">
                    {formatDate(cancellation.createdAt)} – {formatTime(cancellation.createdAt)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Updated</div>
                  <div className="font-semibold">
                    {formatDate(cancellation.updatedAt)} – {formatTime(cancellation.updatedAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Learner Transactions */}
        <div
          ref={learnerBoxRef}
          className={`mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${
            showLearnerBox ? "animate__animated animate__backInRight" : "opacity-0"
          }`}
        >
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold m-0">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full border text-sm font-semibold bg-emerald-50 text-emerald-700 border-emerald-200">
                Learner Transactions
              </span>
            </h2>
          </div>
          <div className="p-6">
            {learnerTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[13px] sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">ID</th>
                      <th className="px-3 py-2 text-left font-semibold">Amount</th>
                      <th className="px-3 py-2 text-left font-semibold">Type</th>
                      <th className="px-3 py-2 text-left font-semibold">Status</th>
                      <th className="px-3 py-2 text-left font-semibold">Reason</th>
                      <th className="px-3 py-2 text-left font-semibold">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learnerTransactions.map((tx) => {
                      const createdDate: Date = new Date((tx as any).createdAt?.toDate?.() || (tx as any).createdAt);
                      return (
                        <tr key={tx.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                          <td className="px-3 py-2 font-mono text-xs" title={tx.id}>
                            {tx.id}
                          </td>
                          <td className="px-3 py-2">
                            <Amount amount={tx.amount} currency={tx.currency} />
                          </td>
                          <td className="px-3 py-2">
                            <Badge label={formatScreamingSnakeCase(tx.type)} classes={transactionTypeStyles[tx.type]} />
                          </td>
                          <td className="px-3 py-2">
                            <Badge
                              label={formatScreamingSnakeCase(tx.status)}
                              classes={transactionStatusStyles[tx.status]}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TransactionLabelPill label={tx.label} />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col leading-tight">
                              <span>{createdDate.toLocaleDateString()}</span>
                              <span className="text-xs text-gray-500">
                                {createdDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-gray-500">No transactions for the learner.</div>
            )}
          </div>
        </div>

        {/* Coach Transactions */}
        <div
          ref={coachBoxRef}
          className={`mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${
            showCoachBox ? "animate__animated animate__backInLeft" : "opacity-0"
          }`}
        >
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold m-0">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full border text-sm font-semibold bg-indigo-50 text-indigo-700 border-indigo-200">
                Coach Transactions
              </span>
            </h2>
          </div>
          <div className="p-6">
            {coachTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[13px] sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">ID</th>
                      <th className="px-3 py-2 text-left font-semibold">Amount</th>
                      <th className="px-3 py-2 text-left font-semibold">Type</th>
                      <th className="px-3 py-2 text-left font-semibold">Status</th>
                      <th className="px-3 py-2 text-left font-semibold">Reason</th>
                      <th className="px-3 py-2 text-left font-semibold">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coachTransactions.map((tx) => {
                      const createdDate: Date = new Date((tx as any).createdAt?.toDate?.() || (tx as any).createdAt);
                      return (
                        <tr key={tx.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                          <td className="px-3 py-2 font-mono text-xs" title={tx.id}>
                            {tx.id}
                          </td>
                          <td className="px-3 py-2">
                            <Amount amount={tx.amount} currency={tx.currency} />
                          </td>
                          <td className="px-3 py-2">
                            <Badge label={formatScreamingSnakeCase(tx.type)} classes={transactionTypeStyles[tx.type]} />
                          </td>
                          <td className="px-3 py-2">
                            <Badge
                              label={formatScreamingSnakeCase(tx.status)}
                              classes={transactionStatusStyles[tx.status]}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TransactionLabelPill label={tx.label} />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col leading-tight">
                              <span>{createdDate.toLocaleDateString()}</span>
                              <span className="text-xs text-gray-500">
                                {createdDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-gray-500">No transactions for the coach.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
